import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Button } from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CalenderIcon } from "../assets/Calendar.svg";
import { ReactComponent as EmailIcon } from "../assets/Email.svg";
import { ReactComponent as PhoneIcon } from "../assets/Phone.svg";
import { ReactComponent as ExpandRightIcon } from "../assets/Expand_right.svg";
import { ReactComponent as LocationIcon } from "../assets/Pin.svg";
import { ReactComponent as UserIcon } from "../assets/User.svg";
import { removeExistingSessionData } from '../lib/helper';

const PatientInfo = ({ onTipClick, patientData }) => {
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);

    useEffect(() => {
        // Get patient data from props or localStorage
        if (patientData) {
            setPatient(patientData);
        } else {
            // Fallback to localStorage if no props passed
            const storedPatientData = localStorage.getItem('patient_data');
            if (storedPatientData) {
                setPatient(JSON.parse(storedPatientData));
            }
        }
    }, [patientData]);

    const tips = [
        "Analyze recent lab trends",
        "Suggest differential diagnosis",
        "Review medication interactions",
        "Recommend care plan adjustments",
        "Identify risk factors",
        "Compare treatment options"
    ];

    const handleChangePatient = () => {
        // Clear patient data but keep access token
        removeExistingSessionData();
        localStorage.removeItem('patient_data');
        // Navigate back to patient search
        navigate('/patient-search');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Show placeholder if no patient data
    if (!patient) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: "max-content", padding: 2 }}>
                <Typography variant="body2" color="gray">
                    No patient selected
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: "max-content" }}>
            {/* Patient Overview */}
            <Box sx={{ bgcolor: '#1f2937', borderRadius: '16px', padding: '16px 24px', border: '1px solid #E2E8F0' }}>
                <Typography variant="subtitle2" color="gray" mb={1}>
                    Patient Overview
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1} className="patient-overview">
                    <Avatar>{patient?.name?.charAt(0) || 'P'}</Avatar>
                    <Box>
                        <Typography>{patient?.name || 'Unknown Patient'}</Typography>
                        <Box display="flex" justifyContent={"space-between"} gap={1}>
                            {patient?.gender && (
                                <Typography className='gender'>{patient.gender}</Typography>
                            )}
                            {patient?.blood_type && (
                                <Typography className='blood-group'>{patient.blood_type}</Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
                <Typography fontSize={16} className="patient-overview" display={"flex"} justifyContent={"space-between"}>
                    <span className='info-icon'>
                        <CalenderIcon />{' '}{formatDate(patient?.birth_date)}
                    </span>
                    {(patient?.phone || patient?.mobile) && (
                        <span className='info-icon'>
                            <PhoneIcon />{' '}{patient.phone || patient.mobile}
                        </span>
                    )}
                </Typography>
                {patient?.email && (
                    <Typography fontSize={16} className="patient-overview">
                        <span className='info-icon'><EmailIcon />{' '}{patient.email}</span>
                    </Typography>
                )}
                {patient?.address && (
                    <Typography fontSize={16} sx={{ padding: '8px' }}>
                        <span className='info-icon'><LocationIcon /> {' '}{patient.address}</span>
                    </Typography>
                )}
                {patient?.mrn && (
                    <Typography fontSize={14} sx={{ padding: '8px', color: '#94A3B8' }}>
                        MRN: {patient.mrn}
                    </Typography>
                )}
            </Box>

            {/* Emergency Contact */}
            {patient?.emergency_contact && (
                <Box sx={{ bgcolor: '#1f2937', mt: 2, borderRadius: '16px', padding: '16px 24px', border: '1px solid #E2E8F0' }}>
                    <Typography variant="subtitle2" color="gray" mb={1}>
                        Emergency Contact
                    </Typography>
                    <Typography fontSize={16} className="patient-overview">
                        <span className='info-icon'>
                            <UserIcon />{' '}
                            {patient.emergency_contact.name}
                            {patient.emergency_contact.relationship && ` (${patient.emergency_contact.relationship})`}
                        </span>
                    </Typography>
                    {patient.emergency_contact.phone && (
                        <Typography fontSize={16} sx={{ padding: '8px' }}>
                            <span className='info-icon'>
                                <PhoneIcon />{' '}{patient.emergency_contact.phone}
                            </span>
                        </Typography>
                    )}
                </Box>
            )}

            {/* Quick Search Tips */}
            <Box sx={{ bgcolor: '#1f2937', mt: 2, borderRadius: '16px', padding: '16px 24px', border: '1px solid #E2E8F0', }}>
                <Typography variant="subtitle2" color="gray" mb={1}>
                    Quick Search Tips
                </Typography>
                <ul style={{ fontSize: '16px', paddingLeft: '0', listStyle: 'none', marginBottom: 0 }}>
                    {tips.map((tip, index) => (
                        <li
                            key={index}
                            className="patient-overview"
                            onClick={() => onTipClick?.(tip)}
                            style={{ cursor: 'pointer', padding: '8px 0', display: 'flex', justifyContent: 'space-between' }}
                        >
                            <span>{tip}</span>  <span><ExpandRightIcon /></span>
                        </li>
                    ))}
                </ul>
            </Box>

            {/* Change Patient Button */}
            <Box sx={{ mt: 2 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SwapHoriz />}
                    onClick={handleChangePatient}
                    sx={{
                        color: '#00D173',
                        borderColor: 'rgba(0, 209, 115, 0.5)',
                        borderRadius: '12px',
                        py: 1.5,
                        fontSize: '16px',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#00D173',
                            background: 'rgba(0, 209, 115, 0.1)',
                        },
                    }}
                >
                    Change Patient
                </Button>
            </Box>
        </Box>
    );
};
export default PatientInfo;